// "Hotel text quote" generator — same additive pattern as flightTextQuote.js
// (Path B: wa.me + editable box + mark-as-sent). Reads ONLY structured
// itinerary.hotels[] fields — never invents a field that doesn't exist
// (room category, extra-bed COST, transfers have no backing field today,
// so they are never printed; the Note block's 3 lines are a fixed,
// deliberately-blank template the agent fills in directly).
import { MEAL_PLANS } from '@/components/ItineraryBuilder/HotelEditor';

function ordinalSuffix(n) {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

// Parsed in UTC (matches how a "YYYY-MM-DD" string is parsed) so this never
// shifts a day depending on the machine's local timezone.
function longDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const day = d.getUTCDate();
  const month = d.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
  return `${day}${ordinalSuffix(day)} ${month} ${d.getUTCFullYear()}`;
}

function computeNights(checkIn, checkOut) {
  if (!checkIn || !checkOut) return null;
  const a = new Date(checkIn);
  const b = new Date(checkOut);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const nights = Math.round((b.getTime() - a.getTime()) / 86400000);
  return nights > 0 ? nights : null;
}

// Meal code + its plain-English definition, both derived from the SAME
// structured MEAL_PLANS label already used by the hotel editor (e.g.
// "MAP (Modified American)") — never a fabricated description. Codes with
// no parenthetical in their label (EPAI, CPAI, MAPAI, APAI) get no definition.
function mealPlanCodeAndDefinition(code) {
  const entry = MEAL_PLANS.find((m) => m.id === code);
  if (!entry) return null;
  const match = /\(([^)]+)\)/.exec(entry.label);
  return { code: entry.id, definition: match ? match[1] : '' };
}

export function buildHotelBlockLines(hotel, { adults, totalCost }) {
  const lines = [];
  lines.push(`*${hotel.hotelName || 'Hotel'}*`);
  lines.push(`Check In - ${longDate(hotel.checkInDate)}`);
  lines.push(`Check Out - ${longDate(hotel.checkOutDate)}`);
  lines.push(`Pax : ${adults || 0} Adults ( + Child If any )`);

  const nights = computeNights(hotel.checkInDate, hotel.checkOutDate);
  lines.push(`Night - ${nights != null ? nights : ''} Nights`);

  const rooms = hotel.rooms ?? hotel.roomCount ?? 1;
  lines.push(`Room : ${rooms} ${hotel.roomType || ''}`.replace(/\s+$/, ''));

  const codes = Array.isArray(hotel.mealPlan) ? hotel.mealPlan : [];
  const parsedMeals = codes.map(mealPlanCodeAndDefinition).filter(Boolean);
  if (parsedMeals.length > 0) {
    const codeStr = parsedMeals.map((m) => m.code).join(' + ');
    const defStr = parsedMeals.map((m) => m.definition).filter(Boolean).join(' / ');
    lines.push(`Meal : ${codeStr}${defStr ? ` ( ${defStr} )` : ''}`);
  }

  const total = Math.round(Number(totalCost) || 0);
  const perNight = nights ? Math.round(total / nights) : null;
  lines.push(`Cost : ${perNight != null ? `₹${perNight.toLocaleString('en-IN')} Per Room Per Night or ` : ''}₹${total.toLocaleString('en-IN')} Total`);

  lines.push('Note :');
  lines.push('* Cancellation Policy -');
  lines.push('* Extra Bed if Any');
  lines.push('* Any Other special inclusions.');
  return lines;
}

/**
 * totalCost MUST already be the selected pricing option's lump-sum total.
 * Multiple hotels each get their own full block (own nights/room/meal),
 * separated by a blank line — the total is the one real package total,
 * never split or estimated per hotel.
 */
export function buildHotelTextQuote({ hotels, adults, totalCost }) {
  const list = (hotels || []).filter((h) => h && h.hotelName);
  if (list.length === 0) return { text: '', hasHotels: false };

  const lines = [];
  list.forEach((h, i) => {
    if (i > 0) lines.push('');
    lines.push(...buildHotelBlockLines(h, { adults, totalCost }));
  });
  return { text: lines.join('\n'), hasHotels: true };
}
